import { useCallback, useEffect, useState } from 'react';
import { getMetrics, type MetricsDigest } from '../../api/admin';
import { ApiError } from '../../api/client';
import { Badge, Button, Card, ErrorState, LoadingState } from '../ui';

type Status = 'loading' | 'ready' | 'error';

/**
 * What this instance has been spending (Phase 33).
 *
 * Phase 31 measured the product on a laptop and left "what a running instance
 * spends" here; Phase 32 could say whether background work was lost but never
 * whether it was merely slow. These are the same stored series a scraper reads
 * from `/metrics`, so the screen and the scrape cannot disagree.
 *
 * A ratio rather than a count wherever one is available: "eleven failures"
 * means nothing without knowing whether it was out of twelve or twelve
 * thousand.
 */
export function AdminMetrics() {
  const [status, setStatus] = useState<Status>('loading');
  const [digest, setDigest] = useState<MetricsDigest | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(() => {
    setStatus('loading');
    getMetrics()
      .then((data) => {
        setDigest(data);
        setStatus('ready');
      })
      .catch((caught: unknown) => {
        setError(caught instanceof ApiError ? caught : null);
        setStatus('error');
      });
  }, []);

  useEffect(load, [load]);

  const failing = digest ? digest.requests.error_ratio : 0;

  return (
    <Card
      title="What this instance is spending"
      description="Requests, background work, and how much of the analysis work was answered twice."
      actions={
        <Button size="sm" variant="ghost" onClick={load}>
          Refresh
        </Button>
      }
    >
      {status === 'loading' && <LoadingState label="Reading the meters…" />}

      {status === 'error' && (
        <ErrorState
          description={error?.message ?? 'The API is not answering.'}
          requestId={error?.requestId}
          onRetry={load}
        />
      )}

      {status === 'ready' && digest && (
        <ul className="sc-checks">
          <li className="sc-checks__item">
            <span className="sc-checks__name">requests measured</span>
            <Badge tone="neutral">{digest.requests.total.toLocaleString()}</Badge>
          </li>
          <li className="sc-checks__item">
            <span className="sc-checks__name">of those, failing</span>
            <Badge
              tone={failing === 0 ? 'success' : failing < 0.01 ? 'warning' : 'danger'}
              srPrefix="failing requests"
            >
              {percent(failing)}
            </Badge>
          </li>
          {digest.slowest_routes.slice(0, 3).map((route) => (
            <li key={route.name} className="sc-checks__item">
              <span className="sc-checks__name">slowest: {route.name}</span>
              <Badge tone="neutral">
                {route.average_ms.toLocaleString()} ms average over {route.calls}
              </Badge>
            </li>
          ))}
          {digest.tasks.slowest.slice(0, 3).map((task) => (
            <li key={task.name} className="sc-checks__item">
              <span className="sc-checks__name">task: {task.name}</span>
              <Badge tone="neutral">
                {task.average_ms.toLocaleString()} ms average over {task.calls}
              </Badge>
            </li>
          ))}
          <li className="sc-checks__item">
            <span className="sc-checks__name">analyses answered from the cache</span>
            <Badge tone="neutral">
              {percent(digest.analyses.cache_ratio)} of{' '}
              {(digest.analyses.computed + digest.analyses.remembered).toLocaleString()}
            </Badge>
          </li>
        </ul>
      )}
    </Card>
  );
}

function percent(ratio: number): string {
  return `${(ratio * 100).toFixed(ratio > 0 && ratio < 0.01 ? 2 : 1)}%`;
}
